/** @jsxImportSource theme-ui @jsxRuntime classic */
import { Section, Box, Text, Flex, IntrinsicImage } from '@tishman/components';
import React, { useMemo } from 'react';

import { formatStoryCategory } from '../utils';

import type { ComponentPropsWithoutRef } from 'react';

interface StoryFeatureStoriesBlockProps {
  data: Queries.SanityStoryQuery;
}

const StoryFeatureStoriesBlock = (
  props: StoryFeatureStoriesBlockProps &
    ComponentPropsWithoutRef<typeof Section>,
): React.JSX.Element => {
  const { featured, story, recentStories } = props.data;

  const relatedStoriesProps = useMemo(() => {
    if (!story?.featuredStories || story?.featuredStories.length === 0) {
      // If the story does not have  featured stories (related stories).
      // display 2 most recent stories in this block instead
      if (!recentStories?.nodes)
        throw new Error('Expected valid recent stories data');

      const stories = recentStories.nodes.map((node) => {
        return {
          title: node?.titleAndSlug?.title,
          excerpt: node?.excerpt,
          url: `/magazine/${encodeURIComponent(
            (node?.category?.toLocaleLowerCase() || '')
              .replace(/\s+/g, '-')
              .replace(/&/g, '')
              .replace(/-+/g, '-'),
          )}/${node?.titleAndSlug?.slug?.current}`,
          alt: node?.poster?.alt,
          image: node?.poster?.asset?.gatsbyImageData,
          category: node?.category,
          formattedPublishAt: node?.formattedPublishAt,
        };
      });

      return {
        title: 'Recent',
        stories,
      };
    }
    // else show the featured stories (related stories) in this block
    const stories = story.featuredStories.map((story) => {
      return {
        title: story?.titleAndSlug?.title,
        excerpt: story?.excerpt,
        url: `/magazine/${formatStoryCategory(story?.category ?? '')}/${
          story?.titleAndSlug?.slug?.current
        }`,
        alt: story?.poster?.alt,
        image: story?.poster?.asset?.gatsbyImageData,
        category: story?.category,
        formattedPublishAt: story?.formattedPublishAt,
      };
    });
    return {
      title: 'Related',
      stories,
    };
  }, [story, recentStories]);

  const featuredStoryProps = useMemo(() => {
    if (!featured?.featuredStory) {
      throw new Error('Expected featured story data');
    }
    const featuredStory = {
      title: featured?.featuredStory?.titleAndSlug?.title,
      excerpt: featured?.featuredStory?.excerpt,
      url: `/magazine/${encodeURIComponent(
        (featured?.featuredStory?.category?.toLocaleLowerCase() || '')
          .replace(/\s+/g, '-')
          .replace(/&/g, '')
          .replace(/-+/g, '-'),
      )}/${featured?.featuredStory?.titleAndSlug?.slug?.current}`,
      alt: featured?.featuredStory?.poster?.alt,
      image: featured?.featuredStory?.poster?.asset?.gatsbyImageData,
      category: featured?.featuredStory?.category,
      formattedPublishAt: featured?.featuredStory?.formattedPublishAt,
    };

    return {
      featuredStory,
    };
  }, [featured]);

  return (
    relatedStoriesProps && (
      <Section data-section="StoryFeaturedRelatedStories" {...props}>
        <Text mt={4} variant="storyLeftMenuHeading">
          Related Articles
        </Text>
        <Flex sx={{ flexDirection: 'column' }}>
          {featuredStoryProps.featuredStory && (
            <Box
              key={featuredStoryProps.featuredStory.title}
              onClick={() =>
                (location.href = `${featuredStoryProps.featuredStory.url}`)
              }
              sx={{
                'pt': 3,
                'pb': 4,
                'borderBottom': '2px solid #D8D8D8',
                'cursor': 'pointer',
                '&:hover h3': {
                  color: 'GREEN',
                  textDecoration: 'underline',
                },
                '&:hover .feature-image': {
                  transform: 'scale(1.05)',
                  opacity: 0.8,
                },
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  mt: '25px',
                  mb: '12px',
                  overflow: 'hidden',
                }}
              >
                {featuredStoryProps.featuredStory.image && (
                  <IntrinsicImage
                    alt={featuredStoryProps.featuredStory.alt ?? ''}
                    className="feature-image"
                    image={featuredStoryProps.featuredStory.image}
                    sx={{
                      transition: '.5s ease all',
                    }}
                  />
                )}
              </Box>
              <Flex sx={{ justifyContent: 'space-between', mb: '12px' }}>
                <Text variant="storyLeftMenuHeading">
                  {featuredStoryProps.featuredStory.category}
                </Text>
                <Text variant="storyLeftMenuHeading">
                  {featuredStoryProps.featuredStory.formattedPublishAt}
                </Text>
              </Flex>
              <Text
                as="h3"
                sx={{
                  fontSize: 3,
                  fontWeight: 'medium',
                }}
              >
                {featuredStoryProps.featuredStory.title}
              </Text>
            </Box>
          )}
          {relatedStoriesProps.stories.map((story) => (
            <Box
              key={story.title}
              onClick={() => (location.href = `${story.url}`)}
              sx={{
                'pt': 3,
                'pb': 4,
                'cursor': 'pointer',
                '&:not(:last-child)': { borderBottom: '2px solid #D8D8D8' },
                '&:hover h3': {
                  color: 'GREEN',
                  textDecoration: 'underline',
                },
              }}
            >
              <Flex sx={{ justifyContent: 'space-between', mb: '12px' }}>
                <Text variant="storyLeftMenuHeading">{story.category}</Text>
                <Text variant="storyLeftMenuHeading">
                  {story.formattedPublishAt}
                </Text>
              </Flex>
              <Text as="h3" sx={{ fontSize: 3, fontWeight: 'medium' }}>
                {story.title}
              </Text>
            </Box>
          ))}
        </Flex>
      </Section>
    )
  );
};

export default StoryFeatureStoriesBlock;
